/**
 * O que o legado guarda no DOM e NINGUÉM limpa: um valor que dura a sessão da página.
 *
 * POR QUE ISTO EXISTE: no legado as 16 abas são um DOM só, do começo ao fim da página. Trocar de
 * aba só troca a classe que mostra (`admin.html:5086-5091`), e abrir outra cliente só troca a aba
 * ativa (`resetarAbas`, `5107-5110`, que "não limpa valor de campo nenhum", `5959-5968`). O que o
 * carregador da aba não reescreve fica: o rascunho da mensagem, o anexo escolhido
 * (`arquivoAnexoAdmin`, variável global, `5744`), o arquivo escolhido em "Enviar arquivo" e as
 * linhas de status (`status-upload-lu`, `status-mensagem-admin`, `status-dossie`).
 *
 * Aqui a moldura desenha só a aba ativa, e a aba que sai da tela desmonta. O estado desses campos,
 * então, não pode morar no componente: mora no módulo, que é o que dura a sessão da página, o mesmo
 * recurso que a moldura usa para a ordem da barra (`ordemDosBotoes`, `../../index.tsx`). Recarregar
 * a página zera tudo, como no legado.
 *
 * DEFEITO DO LEGADO, REPRODUZIDO DE PROPÓSITO: como nada limpa esses campos, eles passam de uma
 * cliente para a outra. Um rascunho, um anexo ou um arquivo escolhido na ficha de uma cliente
 * continuam lá ao abrir a ficha de outra, e o "Enviar" manda para a cliente aberta. A memória
 * NÃO é separada por cliente justamente para isso ser igual; separar é uma linha (a chave da
 * cliente e do lead) e é decisão do fundador.
 */

// A assinatura de uma fonte externa, que o React relê sem rasgar o desenho.
import { useSyncExternalStore } from 'react';

/** Um valor guardado no módulo, com quem quer saber quando ele muda. */
export interface MemoriaDaPagina<T> {
  readonly ler: () => T;
  readonly gravar: (valor: T) => void;
  readonly assinar: (ouvinte: () => void) => () => void;
}

/** Cria uma memória com o valor inicial da página recém-carregada. */
export function criarMemoria<T>(inicial: T): MemoriaDaPagina<T> {
  // O valor vive aqui, fora de qualquer componente, até a página recarregar.
  let valor = inicial;
  // As abas montadas que desenham este valor.
  const ouvintes = new Set<() => void>();
  return {
    ler: () => valor,
    gravar: (novo: T) => {
      valor = novo;
      // Cada aba montada redesenha; a desmontada lê o valor novo quando voltar.
      for (const ouvinte of ouvintes) ouvinte();
    },
    assinar: (ouvinte: () => void) => {
      ouvintes.add(ouvinte);
      return () => {
        ouvintes.delete(ouvinte);
      };
    },
  };
}

/** Lê a memória no desenho e redesenha quando ela muda. */
export function useMemoria<T>(memoria: MemoriaDaPagina<T>): readonly [T, (valor: T) => void] {
  const valor = useSyncExternalStore(memoria.assinar, memoria.ler);
  return [valor, memoria.gravar] as const;
}

/**
 * Uma linha de status que dura a página, com o `mostrar` que apaga sozinho e o `fixar` que não
 * apaga: o mesmo contrato do `useStatusTemporario` de `comum/`, com o texto na memória.
 *
 * O RELÓGIO É DA PÁGINA, e não da aba: no legado o `setTimeout` que apaga "Enviado!" roda com a
 * aba escondida (`admin.html:7765`), e a pessoa que volta depois de 3 s já não vê o texto.
 */
export interface StatusDaPagina {
  readonly memoria: MemoriaDaPagina<string>;
  readonly mostrar: (mensagem: string) => void;
  readonly fixar: (mensagem: string) => void;
}

export function criarStatusDaPagina(
  // 3000 ms: o prazo dos status destas abas no legado (`5416`, `5866`, `7765`).
  prazoMs = 3000,
): StatusDaPagina {
  const memoria = criarMemoria('');
  return {
    memoria,
    mostrar: (mensagem: string) => {
      memoria.gravar(mensagem);
      window.setTimeout(() => {
        // Só apaga se ainda for a mesma: o `if (status.textContent === '...')` do legado.
        if (memoria.ler() === mensagem) memoria.gravar('');
      }, prazoMs);
    },
    // Sem relógio: o legado escreve assim os erros e o "Enviando...", que ficam até a próxima.
    fixar: (mensagem: string) => memoria.gravar(mensagem),
  };
}
